import { Component, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminModalComponent,
} from '../../../../shared/admin';
import {
  FEATURE_REQUEST_PRIORITIES,
  FEATURE_REQUEST_TYPES,
  FEATURE_REQUEST_PRIORITY_LABELS,
  FEATURE_REQUEST_TYPE_LABELS,
  type CreateFeatureRequestPayload,
  type FeatureRequestPriority,
  type FeatureRequestType,
} from '../../../../shared/models/feature-request.models';

const ACCEPTED_MIME = new Set(['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']);
const MAX_BYTES = 10 * 1024 * 1024;

type PendingFile = {
  file: File;
  previewUrl: string | null;
};

@Component({
  selector: 'app-feature-request-create-modal',
  standalone: true,
  imports: [FormsModule, AdminModalComponent, AdminButtonComponent],
  templateUrl: './feature-request-create-modal.component.html',
  styleUrl: './feature-request-create-modal.component.scss',
})
export class FeatureRequestCreateModalComponent {
  readonly open = input(false);
  readonly busy = input(false);
  readonly error = input<string | null>(null);

  readonly closed = output<void>();
  readonly submitted = output<{ payload: CreateFeatureRequestPayload; files: File[] }>();

  readonly types = FEATURE_REQUEST_TYPES;
  readonly priorities = FEATURE_REQUEST_PRIORITIES;
  readonly typeLabels = FEATURE_REQUEST_TYPE_LABELS;
  readonly priorityLabels = FEATURE_REQUEST_PRIORITY_LABELS;

  title = '';
  type: FeatureRequestType = 'FEATURE';
  priority: FeatureRequestPriority = 'MEDIUM';
  description = '';
  readonly files = signal<PendingFile[]>([]);
  readonly fieldError = signal<string | null>(null);

  onClose(): void {
    this.reset();
    this.closed.emit();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    input.value = '';

    const next = [...this.files()];
    for (const file of selected) {
      if (!ACCEPTED_MIME.has(file.type)) {
        this.fieldError.set('Only PNG, JPG, JPEG, and PDF files are allowed.');
        continue;
      }
      if (file.size > MAX_BYTES) {
        this.fieldError.set('Each file must be 10 MB or smaller.');
        continue;
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      next.push({ file, previewUrl });
    }
    this.files.set(next);
  }

  removeFile(index: number): void {
    const next = [...this.files()];
    const removed = next.splice(index, 1)[0];
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    this.files.set(next);
  }

  onSubmit(): void {
    this.fieldError.set(null);
    if (!this.title.trim()) {
      this.fieldError.set('Title is required.');
      return;
    }
    if (!this.description.trim()) {
      this.fieldError.set('Description is required.');
      return;
    }

    this.submitted.emit({
      payload: {
        title: this.title.trim(),
        type: this.type,
        priority: this.priority,
        description: this.description.trim(),
      },
      files: this.files().map((f) => f.file),
    });
  }

  reset(): void {
    for (const f of this.files()) {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    }
    this.title = '';
    this.type = 'FEATURE';
    this.priority = 'MEDIUM';
    this.description = '';
    this.files.set([]);
    this.fieldError.set(null);
  }

  isPdf(file: File): boolean {
    return file.type === 'application/pdf';
  }
}
